<script setup lang="ts">
import {reactive} from "vue";
import useUpcomingSequenceMessages from "@/notification-sequences/composables/useUpcomingSequenceMessages";
import ConsumerAnimationCell from "@/core/components/animation/ConsumerAnimationCell.vue";
import {Badge} from "@/core/components/ui/badge";
import dateHelper from "@/core/helpers/dateHelper";
import {UpcomingSequenceMessage} from "@/notification-sequences/interfaces/NotificationSequence";
import {ExternalLink} from "@lucide/vue";
import TemplatePreviewTooltip from "@/notification-template/components/preview/TemplatePreviewTooltip.vue";

const params = reactive({
    page: 1,
    limit: 10
});

const {data, isValidating} = useUpcomingSequenceMessages(params);

const headers = [
    {title: "Prévu le", key: "send_at", sortable: false},
    {title: "Patient", key: "consumer", sortable: false, width: "30%"},
    {title: "RCS", key: "template", sortable: false},
    {title: "Séquence", key: "sequence", nowrap: true, sortable: false},
];

/**
 * Upcoming messages are not materialized yet: the same message can appear
 * once per sequence instance.
 */
const rowKey = (item: UpcomingSequenceMessage) => {
    return `${item.sequence_instance_id}-${item.id}`;
};

const handlePageChange = (page: number) => {
    params.page = page;
};

const handleItemPerPageUpdate = (limit: number) => {
    params.page = 1;
    params.limit = limit;
};
</script>

<template>
  <v-data-table-server
    :loading="isValidating"
    :headers="headers"
    :items="data?.data"
    :item-value="rowKey"
    :items-length="data?.meta?.total ?? 0"
    :items-per-page-options="[10, 20, 30]"
    :items-per-page="params.limit"
    :page="params.page"
    no-data-text="Aucun message à venir"
    @update:items-per-page="handleItemPerPageUpdate"
    @update:page="handlePageChange"
  >
    <template #[`item.consumer`]="{item}">
      <ConsumerAnimationCell
        :name="item.consumer?.information?.name ?? 'Anonyme'"
        :birth-date="item.consumer?.information?.birth_date"
        :phone="item.customer?.information?.phone"
        :onboarded="item.customer?.onboarded"
        :editable="false"
      />
    </template>
    <template #[`item.template`]="{item}">
      <div class="flex items-center gap-4">
        <Badge variant="secondary" class="whitespace-nowrap">
          {{ item.title }}
        </Badge>
        <TemplatePreviewTooltip v-if="item.template" :template="item.template" />
      </div>
    </template>
    <template #[`item.sequence`]="{item}">
      <router-link
        :to="{
          name: 'patient-administration.communication.sequences.details',
          params: {sequenceId: item.sequence.id}
        }"
        class="text-primary font-medium hover:underline inline-flex items-center gap-1"
      >
        {{ item.sequence.name }}
        <ExternalLink class="size-3.5 shrink-0" />
      </router-link>
    </template>
    <template #[`item.send_at`]="{item}">
      <span class="text-primary font-medium whitespace-nowrap">
        {{ dateHelper.humanDayTime(item.send_at) }}
      </span>
    </template>
  </v-data-table-server>
</template>
